import { epochOfWallMin } from '../time/personal-day';
import { checkinOccurrenceKey, parseCheckinOccurrenceKey } from './checkin-day';
import {
  epochOfLocalDateTime,
  isLocalDateTime,
  occurrencesInPersonalDay,
  readCheckinSchedule,
  type LocalDateTime,
  type OccurrenceItemData,
} from './checkin-occurrence';
import { readPlannedMin } from './checkin-plan';
import { MINUTES_PER_DAY, MS_PER_MINUTE } from './constants';
import { ReminderScope, type ReminderLead } from './enums';

/**
 * Planned-time reminders of check-in items (docs/requirements/checkin-reminder.md). Pure
 * functions: the caller passes the clock reading and the per-device reminder state, and gets
 * back what to show and the next state. Instants are derived from the personal date and the
 * planned time on the current local wall clock at each call, so a time-zone change or a
 * daylight-saving switch is picked up on the next call (time-drift D3, D4).
 */

/** A due reminder later than this after its time is a missed one (background timers run about once a minute). */
export const REMINDER_LATE_MS = 2 * MS_PER_MINUTE;
/** Minutes of the "remind me later" action. */
export const REMINDER_SNOOZE_MIN = 10;

/** The fields a reminder reads from a check-in item record. */
export interface ReminderItemData extends OccurrenceItemData {
  readonly name: string;
  readonly remind?: unknown;
}

export interface ReminderItem {
  readonly id: string;
  readonly data: ReminderItemData;
}

export interface ReminderSettings {
  readonly dayStartMin: number;
  /** Day start that interprets legacy check-in data (setting `checkinLegacyDayStartMin`). */
  readonly legacyDayStartMin: number;
  readonly reminderScope: ReminderScope;
  readonly reminderLeadMin: ReminderLead;
}

/** Whether the item is picked for reminders under the `selected` scope (only `true` counts). */
export function readCheckinRemind(data: { readonly remind?: unknown }): boolean {
  return data.remind === true;
}

/** Whether the item takes part in reminders: active and inside the scope (planned time aside). */
function inReminderScope(data: ReminderItemData, scope: ReminderScope): boolean {
  return data.active !== false && (scope === ReminderScope.All || readCheckinRemind(data));
}

/** Whether the item reminds at all: active, with a (current) planned time, and inside the scope. */
export function checkinReminds(data: ReminderItemData, scope: ReminderScope): boolean {
  return readPlannedMin(data) !== null && inReminderScope(data, scope);
}

/**
 * The instant of an item's planned time on `personalDate`. A planned time earlier than the
 * day start belongs to the next calendar day (requirements decision 2: 02:00 on Friday's
 * personal day is Saturday 02:00).
 */
export function plannedInstantOf(personalDate: string, plannedMin: number, dayStartMin: number): number {
  return epochOfWallMin(personalDate, plannedMin < dayStartMin ? plannedMin + MINUTES_PER_DAY : plannedMin);
}

/** Per-device identity of one reminder: the occurrence (`{itemId}|{occursAt}`, data dictionary §3.2). */
export const reminderKey = (itemId: string, occursAt: LocalDateTime): string =>
  checkinOccurrenceKey(itemId, occursAt);

/**
 * Item and occurrence of a reminder key, or null when it is not one. A key of the format before
 * occurrences (`{personalDate}|{itemId}`) reads as null: its notification can no longer be acted on.
 */
export function parseReminderKey(
  key: string,
): { readonly itemId: string; readonly occursAt: LocalDateTime } | null {
  const parsed = parseCheckinOccurrenceKey(key);
  if (parsed === null || !isLocalDateTime(parsed.occurrence)) return null;
  return { itemId: parsed.itemId, occursAt: parsed.occurrence };
}

const LEGACY_REMINDER_KEY = /^(\d{4}-\d{2}-\d{2})\|(.+)$/;

/**
 * Personal date and item of a key of the format before occurrences (`{personalDate}|{itemId}`),
 * carried by a notification shown before the upgrade; null for any other text.
 */
export function parseLegacyReminderKey(
  key: string,
): { readonly personalDate: string; readonly itemId: string } | null {
  const match = LEGACY_REMINDER_KEY.exec(key);
  return match === null ? null : { personalDate: match[1]!, itemId: match[2]! };
}

/**
 * The occurrence a pre-upgrade reminder key stands for: the item's first timed occurrence of
 * that personal day (a notification's "Done" button keeps working after the upgrade), or null.
 */
export function legacyReminderOccurrence(
  item: ReminderItem,
  personalDate: string,
  settings: Pick<ReminderSettings, 'dayStartMin' | 'legacyDayStartMin'>,
): LocalDateTime | null {
  const versions = readCheckinSchedule(item.data, settings.legacyDayStartMin);
  const slots = occurrencesInPersonalDay(item.data, versions, personalDate, settings.dayStartMin);
  return slots.find((slot) => slot.occursAt !== null)?.occursAt ?? null;
}

export interface ReminderSlot {
  readonly key: string;
  readonly itemId: string;
  readonly name: string;
  /** Personal date the occurrence is shown on (under the current day start). */
  readonly personalDate: string;
  readonly occursAt: LocalDateTime;
  readonly plannedMin: number;
  /** Epoch ms of the planned time. */
  readonly plannedAt: number;
  /** Epoch ms when the reminder is due: planned time minus the lead. */
  readonly fireAt: number;
}

/** The reminders of the personal days `dates`: one per timed occurrence of an item that reminds. */
export function reminderSlots(
  items: readonly ReminderItem[],
  dates: readonly string[],
  settings: ReminderSettings,
): ReminderSlot[] {
  return dates.flatMap((personalDate) =>
    items.flatMap((item) => {
      if (!inReminderScope(item.data, settings.reminderScope)) return [];
      const versions = readCheckinSchedule(item.data, settings.legacyDayStartMin);
      return occurrencesInPersonalDay(item.data, versions, personalDate, settings.dayStartMin).flatMap(
        (slot) => {
          if (slot.occursAt === null || slot.plannedMin === null) return [];
          const plannedAt = epochOfLocalDateTime(slot.occursAt);
          return [
            {
              key: reminderKey(item.id, slot.occursAt),
              itemId: item.id,
              name: item.data.name,
              personalDate,
              occursAt: slot.occursAt,
              plannedMin: slot.plannedMin,
              plannedAt,
              fireAt: plannedAt - settings.reminderLeadMin * MS_PER_MINUTE,
            },
          ];
        },
      );
    }),
  );
}

/** Per-device memory of reminders already shown and of postponed ones (data dictionary §7). */
export interface ReminderState {
  /** Reminder key → epoch ms the reminder was due when it was handled (shown or recorded). */
  readonly fired: Readonly<Record<string, number>>;
  /** Reminder key → epoch ms of the postponed reminder. */
  readonly snoozed: Readonly<Record<string, number>>;
  /** Epoch ms of the last check on this device; null before the first one. */
  readonly lastCheckAt: number | null;
  /**
   * Keys `personalDate|itemId` the format before occurrences had shown, kept until the next plan
   * adopts them for each item's first occurrence of that day (no reminder is shown twice).
   */
  readonly legacyFired?: readonly string[];
  /** Postponed reminders of that format (`personalDate|itemId` → epoch ms), adopted the same way. */
  readonly legacySnoozed?: Readonly<Record<string, number>>;
}

export const EMPTY_REMINDER_STATE: ReminderState = { fired: {}, snoozed: {}, lastCheckAt: null };

/** Handled and postponed reminders older than this are forgotten. */
const REMINDER_MEMORY_MS = 3 * 24 * 60 * MS_PER_MINUTE;

export interface ReminderPlanInput {
  /** Slots of yesterday's (for postponed reminders across the day start), today's and tomorrow's personal dates. */
  readonly slots: readonly ReminderSlot[];
  /** Keys of the slots whose check-in is already ticked. */
  readonly doneKeys: ReadonlySet<string>;
  readonly state: ReminderState;
  readonly now: number;
  /** Today's personal date: missed reminders are summarised only for it. */
  readonly today: string;
}

export interface ReminderPlan {
  /** Reminders to show now, grouped by planned time (one notification per group). */
  readonly onTime: readonly (readonly ReminderSlot[])[];
  /** Missed reminders of today's personal day (one summary notification). */
  readonly missed: readonly ReminderSlot[];
  readonly state: ReminderState;
  /** Epoch ms of the next pending reminder, or null when none is left. */
  readonly nextAt: number | null;
}

/**
 * Decide which reminders to show at `now`. A reminder is pending until it has been handled at
 * its current due time: `fired` remembers the due time it was handled at, so a later due time
 * (the planned time or the lead changed after it fired) makes it pending again. A due reminder
 * is shown on time when it is at most REMINDER_LATE_MS late or its planned time has not passed
 * yet. Otherwise it is missed — joins the summary — only when it fell due after the previous
 * check (the page was not running then) and belongs to today's personal day. A late slot that
 * was already due at the previous check only appeared since (an item created or edited after
 * its time, a check-in unticked, a scope change, a synced record) and is recorded without being
 * shown. Ticked occurrences never remind. Memory older than REMINDER_MEMORY_MS is pruned.
 */
export function planReminders({ slots, doneKeys, state, now, today }: ReminderPlanInput): ReminderPlan {
  const fired = new Map(Object.entries(state.fired));
  const snoozed = new Map(Object.entries(state.snoozed));
  const legacy = new Set(state.legacyFired ?? []);
  const legacySnoozed = new Map(Object.entries(state.legacySnoozed ?? {}));
  if (legacy.size > 0 || legacySnoozed.size > 0) {
    const adopted = new Set<string>();
    for (const slot of [...slots].sort((a, b) => a.plannedAt - b.plannedAt)) {
      const legacyKey = `${slot.personalDate}|${slot.itemId}`;
      if (adopted.has(legacyKey)) continue;
      adopted.add(legacyKey);
      if (legacy.has(legacyKey) && !fired.has(slot.key)) fired.set(slot.key, slot.fireAt);
      const until = legacySnoozed.get(legacyKey);
      if (until !== undefined && !snoozed.has(slot.key)) snoozed.set(slot.key, until);
    }
  }
  const onTime: ReminderSlot[] = [];
  const missed: ReminderSlot[] = [];
  let nextAt: number | null = null;
  for (const slot of slots) {
    if (doneKeys.has(slot.key)) {
      snoozed.delete(slot.key);
      continue;
    }
    const handledAt = fired.get(slot.key);
    const dueAt =
      snoozed.get(slot.key) ?? (handledAt !== undefined && handledAt >= slot.fireAt ? null : slot.fireAt);
    if (dueAt === null) continue;
    if (now < dueAt) {
      nextAt = nextAt === null ? dueAt : Math.min(nextAt, dueAt);
      continue;
    }
    const pageWasAway = state.lastCheckAt !== null && dueAt > state.lastCheckAt;
    if (now - dueAt <= REMINDER_LATE_MS || now < slot.plannedAt) onTime.push(slot);
    else if (pageWasAway && slot.personalDate === today) missed.push(slot);
    fired.set(slot.key, Math.max(dueAt, slot.fireAt));
    snoozed.delete(slot.key);
  }
  const keep = ([, at]: [string, number]): boolean => at >= now - REMINDER_MEMORY_MS;
  const groups = new Map<number, ReminderSlot[]>();
  for (const slot of onTime) groups.set(slot.plannedAt, [...(groups.get(slot.plannedAt) ?? []), slot]);
  return {
    onTime: [...groups.entries()].sort(([a], [b]) => a - b).map(([, group]) => group),
    missed: [...missed].sort((a, b) => a.plannedAt - b.plannedAt),
    state: {
      fired: Object.fromEntries([...fired].filter(keep).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))),
      snoozed: Object.fromEntries([...snoozed].filter(keep)),
      lastCheckAt: now,
    },
    nextAt,
  };
}

/** State after "remind me later" on `keys`: due again `REMINDER_SNOOZE_MIN` minutes after `now`. */
export function snoozeReminders(state: ReminderState, keys: readonly string[], now: number): ReminderState {
  const at = now + REMINDER_SNOOZE_MIN * MS_PER_MINUTE;
  return {
    ...state,
    snoozed: { ...state.snoozed, ...Object.fromEntries(keys.map((key) => [key, at])) },
  };
}

/**
 * State after some notifications of a plan could not be shown (permission revoked, the service
 * worker not ready): their keys are taken out of `fired` again and the check time is kept, so
 * the next check shows them, late ones as missed.
 */
export function unfireReminders(
  planned: ReminderState,
  previous: ReminderState,
  keys: readonly string[],
): ReminderState {
  if (keys.length === 0) return planned;
  const failed = new Set(keys);
  return {
    ...planned,
    fired: Object.fromEntries(Object.entries(planned.fired).filter(([key]) => !failed.has(key))),
    lastCheckAt: previous.lastCheckAt,
  };
}

const finiteEntries = (value: unknown): [string, number][] =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? Object.entries(value).filter(
        (entry): entry is [string, number] => typeof entry[1] === 'number' && Number.isFinite(entry[1]),
      )
    : [];

/**
 * Parse a stored reminder state; anything malformed reads as empty (device-local data, never
 * trusted). The format before occurrences (`fired` as a list of `personalDate|itemId`) is kept
 * as `legacyFired` (and its snoozes as `legacySnoozed`) for the next plan to adopt.
 */
export function readReminderState(value: unknown): ReminderState {
  if (typeof value !== 'object' || value === null) return EMPTY_REMINDER_STATE;
  const { fired, snoozed, lastCheckAt } = value as {
    fired?: unknown;
    snoozed?: unknown;
    lastCheckAt?: unknown;
  };
  const isOccurrence = ([key]: [string, number]): boolean => parseReminderKey(key) !== null;
  const legacyFired = Array.isArray(fired)
    ? fired.filter((key): key is string => typeof key === 'string' && parseLegacyReminderKey(key) !== null)
    : [];
  const legacySnoozed = Object.fromEntries(
    finiteEntries(snoozed).filter(([key]) => parseLegacyReminderKey(key) !== null),
  );
  return {
    ...(legacyFired.length === 0 ? {} : { legacyFired }),
    ...(Object.keys(legacySnoozed).length === 0 ? {} : { legacySnoozed }),
    fired: Object.fromEntries(finiteEntries(fired).filter(isOccurrence)),
    snoozed: Object.fromEntries(finiteEntries(snoozed).filter(isOccurrence)),
    lastCheckAt: typeof lastCheckAt === 'number' && Number.isFinite(lastCheckAt) ? lastCheckAt : null,
  };
}
